/**
 * Shop-state store — the single transport-agnostic ingest point for the
 * touch-alive dashboard (architecture.md §4.5). Today it polls
 * `GET /api/dashboard/shop-state`; when pushed-alive lands, a WebSocket
 * feed drives the SAME store via `ingestShopState` and nothing in the UI
 * has to change.
 *
 * Types here MIRROR `worker/lib/shop-state/types.ts` (same manual-mirror
 * pattern as `src/components/dashboard/agentic/types.ts`) — kept in sync
 * by hand rather than shared via the published package, since the shape
 * is dashboard-internal.
 */

import { useAuth } from '@clerk/clerk-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { authFetch } from '@/lib/api'

export type ReflexKind =
  | 'arriving_soon'
  | 'overdue'
  | 'running_long'
  | 'nearing_end'
  | 'inbound_text'

export type ActionKind =
  | 'mark_arrived'
  | 'mark_no_show'
  | 'complete_appointment'
  | 'review_inbound_text'

export type AppointmentTarget = {
  type: 'appointment'
  appointmentId: string
  customerName: string | null
  vehicleLabel: string | null
  startsAtIso: string
  endsAtIso: string
}

export type InboundTextTarget = {
  type: 'inbound_text'
  requestId: string
  customerName: string | null
  fromE164: string
  preview: string
  receivedAtIso: string
}

export type ActionTarget = AppointmentTarget | InboundTextTarget

export type ActionExecute = {
  method: 'PATCH'
  path: string
  body: Record<string, string>
}

export type ShopStateAction = {
  id: string
  reflex: ReflexKind
  kind: ActionKind
  priority: number
  target: ActionTarget
  /** Null for informational cards (review_inbound_text) — nothing to commit. */
  execute: ActionExecute | null
  computedAt: string
  validUntil: string | null
  supersededBy: string | null
}

/**
 * An action with a pre-bound call — what the one-tap surfaces execute.
 * Frontend-only convenience narrowing (no worker counterpart): the resolver
 * builds actions; only the client needs the "this one is tappable" view.
 */
export type ExecutableShopStateAction = ShopStateAction & {
  execute: ActionExecute
  target: AppointmentTarget
}

/** Type guard the tap surfaces share — narrows in place, no re-synthesis. */
export function isExecutableAction(
  a: ShopStateAction,
): a is ExecutableShopStateAction {
  return a.execute !== null && a.target.type === 'appointment'
}

export type ShopLoad = {
  load_pct: number
  queued_hours: number
  capacity_hours: number
  bays: number
  appointment_count: number
}

export type ShopStateContext = {
  nowIso: string
  load: ShopLoad
  counts: {
    arrivingSoon: number
    overdue: number
    runningLong: number
    nearingEnd: number
    active: number
  }
}

export type ShopState = {
  context: ShopStateContext
  rankedActions: ShopStateAction[]
}

/** TanStack Query key — exported so action handlers invalidate the same store. */
export const SHOP_STATE_QUERY_KEY = ['shop-state'] as const

/**
 * The single ingest seam (architecture.md §4.5). Today the only
 * transport is the poll, which delivers a full snapshot, so we replace
 * wholesale. When pushed-alive lands, a WebSocket delta would merge into
 * `prev` here (apply `supersededBy`, splice changed actions) — the UI
 * stays transport-agnostic because it only ever reads the store this
 * function produces. Pure: no side effects.
 */
export function ingestShopState(
  prev: ShopState | undefined,
  next: ShopState,
): ShopState {
  void prev // full-snapshot transport today; reserved for delta merges
  return next
}

/**
 * Read the shop state. "Touch-alive": runs on mount, on a cheap 60s
 * poll, and when the user returns to the tab. Action handlers invalidate
 * `SHOP_STATE_QUERY_KEY` to force a re-resolve right after a write.
 */
export function useShopState() {
  const { getToken, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: SHOP_STATE_QUERY_KEY,
    enabled: isLoaded,
    queryFn: async () => {
      const next = await authFetch<ShopState>(
        '/api/dashboard/shop-state',
        getToken,
      )
      return ingestShopState(
        queryClient.getQueryData<ShopState>(SHOP_STATE_QUERY_KEY),
        next,
      )
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}
